/**
 * HotSpotModal
 *
 * Shows interior visualisation images for a given hotspot camera.
 * Uses Modal (centered overlay) on both desktop and mobile.
 *
 * Features:
 *  - Cycle through styles via click or arrow keys
 *  - Skeleton while image loads to prevent layout jump
 *  - AssetLoadError on image load failure
 *  - BimCadaplusLogo overlay on image (bottom-left)
 *  - Optional: "?" tooltip with style description (title attr until Tooltip is ported)
 *  - Optional: maximize button (requestFullscreen on image)
 *  - Optional: AI disclaimer below image
 *  - Controlled by panelSettings.unit_sheet_mode_hotspot.hotspot_modal flags
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react'

import { useI18n }    from '../../../../shared/i18n/useI18n.js'

import { useApp }     from '../../../../app/AppContext.jsx'
import { useCatalog } from '../../../catalog/index.js'

import { AssetLoadError }    from '../../../../shared/ui/AssetLoadError/index.js'
import { BimCadaplusLogoTag } from '../../../../shared/ui/BimCadaplusLogoTag/index.js'
import { Button }            from '../../../../shared/ui/Button/index.js'
import { Modal }             from '../../../../shared/ui/Modal/index.js'
import { Skeleton }          from '../../../../shared/ui/Skeleton/index.js'
import { Maximize, QuestionMarkBubble, ArrowUp } from '../../../../shared/ui/icons/index.js'

import styles from './HotSpotModal.module.scss'

function buildInteriorImageUrl(projectDataUrl, fileName) {
  if (!projectDataUrl || !fileName) return null
  const base = projectDataUrl.endsWith('/') ? projectDataUrl : `${projectDataUrl}/`
  return `${base}interiors/${fileName}`
}

function extractCompartmentIdFromHotspotName(name) {
  const match = /^interior_([^_]+)_/.exec(name ?? '')
  return match ? match[1] : null
}

// ── Helper: navigation angles between hotspots ────────────────────────────────

/**
 * computeHotspotNavigation — for each other hotspot, calculates its signed angle
 * relative to the current camera's forward direction and classifies as left or right.
 *
 * We work in the same Y-DOWN display space used by SVG rendering: JSON camera coords
 * are flipped on Y, matching the hotspot renderer's translate(x, -y) logic.
 *
 * Negative angle = counter-clockwise from forward = viewer's LEFT.
 * Positive angle = clockwise from forward = viewer's RIGHT.
 *
 * @param {{ position: number[], target: number[] }} current
 * @param {Array<{ name: string, position: number[], target: number[] }>} all
 * @returns {Array<{hotspot: object, signedAngle: number}>}
 */
function computeHotspotNavigation(current, all) {
  const [px, py] = current.position
  const [tx, ty] = current.target

  // Work in SVG display space (Y-DOWN), matching the scale(1,-1) transform on apartment_tier.
  // JSON camera coords are cartesian Y-UP, so flip Y before all vector math.
  const fwdX = tx - px
  const fwdY = -(ty - py)
  const fwdLen = Math.sqrt(fwdX * fwdX + fwdY * fwdY)
  if (fwdLen < 0.001) return []
  const fnX = fwdX / fwdLen
  const fnY = fwdY / fwdLen

  const items = []

  for (const hotspot of all) {
    if (hotspot.name === current.name) continue
    const [hx, hy] = hotspot.position
    const relX = hx - px
    const relY = -(hy - py)
    const relLen = Math.sqrt(relX * relX + relY * relY)
    if (relLen < 0.001) continue
    const rnX = relX / relLen
    const rnY = relY / relLen

    // 2D cross product in Y-DOWN space: positive cross = CW = viewer's RIGHT
    const cross = fnX * rnY - fnY * rnX
    const dot   = fnX * rnX + fnY * rnY
    const signedAngle = Math.atan2(cross, dot) * (180 / Math.PI)

    items.push({ hotspot, signedAngle })
  }

  // Render left-to-right by bearing relative to current camera axis.
  items.sort((a, b) => a.signedAngle - b.signedAngle)

  return items
}

// ── Sub-component: hotspot navigation overlay ─────────────────────────────────

/**
 * HotspotNavigator — renders directional nav buttons at the bottom-center of the image.
 *
 * Arrow rotation formula: CSS rotate(signedAngle deg) maps ArrowUp icon directly
 * in the same Y-DOWN display space used for the signed-angle calculation.
 *
 * @param {{ hotspot: object, allHotspots: object[], onSelect: (name: string) => void }} props
 */
function HotspotNavigator({ hotspot, allHotspots, onSelect }) {
  const { t } = useI18n()
  const navItems = useMemo(
    () => computeHotspotNavigation(hotspot, allHotspots),
    [hotspot, allHotspots],
  )

  if (navItems.length === 0) return null

  const stopEvents = (e) => { e.stopPropagation(); e.preventDefault() }
  const getHotspotLabel = (hotspotItem) => {
    const compartmentId = extractCompartmentIdFromHotspotName(hotspotItem?.name)
    if (!compartmentId) return hotspotItem?.name ?? ''
    return t(`compartments.${compartmentId}`)
  }

  return (
    <div className={styles.navigator}>
      {navItems.map(({ hotspot: item, signedAngle }) => {
        const label = getHotspotLabel(item)
        const isLeftOfAxis = signedAngle <= 0

        return (
          <Button
            key={item.name}
            type="button"
            kind="text"
            size="medium"
            className={styles.navBtn}
            onClick={(e) => { stopEvents(e); onSelect(item.name) }}
            onPointerDown={stopEvents}
            aria-label={label}
          >
            {isLeftOfAxis ? (
              <>
                <span className={styles.navLabel}>{label}</span>
                <ArrowUp
                  className={styles.navArrow}
                  style={{ transform: `rotate(${signedAngle}deg)` }}
                  aria-hidden="true"
                />
              </>
            ) : (
              <>
                <ArrowUp
                  className={styles.navArrow}
                  style={{ transform: `rotate(${signedAngle}deg)` }}
                  aria-hidden="true"
                />
                <span className={styles.navLabel}>{label}</span>
              </>
            )}
          </Button>
        )
      })}
    </div>
  )
}

// ── Sub-component: interior image with Skeleton / error fallback ──────────────

function InteriorImage({ src, alt, imgRef, isFlipped, onClick, onPointerDown, stageStyle, maximized, navigator }) {
  const { logoSrc } = useApp()
  const [loaded, setLoaded]   = useState(false)
  const [errored, setErrored] = useState(false)

  const handleLoad  = useCallback(() => setLoaded(true),  [])
  const handleError = useCallback(() => { setLoaded(true); setErrored(true) }, [])

  return (
    <div
      className={[styles.imageWrap, maximized ? styles.imageWrapMaximized : null].filter(Boolean).join(' ')}
      onClick={onClick}
      onPointerDown={onPointerDown}
      style={stageStyle}
    >
      {!loaded && <Skeleton className={styles.imageSkeleton} />}

      {errored ? (
        <AssetLoadError
          logoSrc={logoSrc}
          className={styles.imageError}
        />
      ) : (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={[styles.image, loaded ? styles.imageVisible : styles.imageHidden].join(' ')}
          onLoad={handleLoad}
          onError={handleError}
          draggable={false}
          style={isFlipped ? { transform: 'scaleX(-1)' } : {}}
        />
      )}

      {/* Logo tag — bottom-left corner of the image */}
      <BimCadaplusLogoTag size="small" position="bottom" className={styles.logoOverlay} />

      {/* Hotspot navigation overlay — bottom-center */}
      {navigator}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.hotspot         — camera object from apartmentHotSpots
 * @param {boolean}  props.isMobile
 * @param {object}   [props.modalConfig]      — panelSettings.unit_sheet_mode_hotspot.hotspot_modal
 * @param {object[]} [props.allHotspots]      — full list of interior hotspots for this tier (navigation)
 * @param {(name: string) => void} [props.onHotspotSelect] — called when user navigates to another hotspot
 */
export function HotSpotModal({ isOpen, onClose, hotspot, isMobile, modalConfig, allHotspots, onHotspotSelect }) {
  const { t }           = useI18n()
  const { interiorsData } = useCatalog()
  const { windowData } = useApp()

  const imgRef = useRef(null)
  const dragRef = useRef(null)
  const panRef = useRef({ x: 0, y: 0 })
  const suppressNextClickRef = useRef(false)

  // ── Derived: styles list for this hotspot ────────────────────────────────

  const stylesList = hotspot ? (interiorsData?.[hotspot.name] ?? []) : []
  const isFlipped = hotspot?.focal_length < 0 ?? false

  const [activeStyleIndex, setActiveStyleIndex] = useState(0)
  const [isMaximized, setIsMaximized] = useState(false)
  const [pan, setPan] = useState({ x: 0, y: 0 })

  // Reset to first style when hotspot changes
  useEffect(() => {
    setActiveStyleIndex(0)
    setIsMaximized(false)
    setPan({ x: 0, y: 0 })
  }, [hotspot?.name])

  // Keyboard navigation: Arrow Left/Right to cycle styles
  useEffect(() => {
    if (!isOpen || stylesList.length <= 1) return

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setActiveStyleIndex((i) => (i - 1 + stylesList.length) % stylesList.length)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setActiveStyleIndex((i) => (i + 1) % stylesList.length)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, stylesList.length])

  useEffect(() => {
    panRef.current = pan
  }, [pan])

  const activeStyle = stylesList[activeStyleIndex] ?? null

  // ── Config flags ─────────────────────────────────────────────────────────

  const showAiDisclaimer     = modalConfig?.show_ai_disclaimer     ?? false
  const showStyleDescription = modalConfig?.show_style_description ?? false
  const allowMaximize        = modalConfig?.allow_maximize         ?? false

  const handleCycleStyle = useCallback(() => {
    if (stylesList.length <= 1) return
    setActiveStyleIndex((currentIndex) => (currentIndex + 1) % stylesList.length)
  }, [stylesList.length])

  // ── Fullscreen handler ───────────────────────────────────────────────────

  const handleMaximize = useCallback(() => {
    setIsMaximized((current) => {
      const next = !current
      if (!next) setPan({ x: 0, y: 0 })
      return next
    })
  }, [])

  const handleImagePointerDown = useCallback((event) => {
    if (!isMaximized || event.button !== 0) return

    event.preventDefault()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startPanX: panRef.current.x,
      startPanY: panRef.current.y,
      moved: false,
    }

    const handleMove = (moveEvent) => {
      const state = dragRef.current
      if (!state || moveEvent.pointerId !== state.pointerId) return

      const deltaX = moveEvent.clientX - state.startX
      const deltaY = moveEvent.clientY - state.startY

      if (!state.moved && Math.abs(deltaX) + Math.abs(deltaY) > 4) {
        state.moved = true
      }

      setPan({
        x: state.startPanX + deltaX,
        y: state.startPanY + deltaY,
      })
    }

    const handleUp = (upEvent) => {
      const state = dragRef.current
      if (!state || upEvent.pointerId !== state.pointerId) return
      if (state.moved) {
        suppressNextClickRef.current = true
      }
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      dragRef.current = null
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
  }, [isMaximized])

  const handleImageClick = useCallback(() => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false
      return
    }
    if (dragRef.current?.moved) {
      dragRef.current.moved = false
      return
    }
    handleCycleStyle()
  }, [handleCycleStyle])

  // ── Image URL ─────────────────────────────────────────────────────────────

  const imageUrl = buildInteriorImageUrl(windowData?.projectDataUrl ?? null, activeStyle?.file)

  const styleName   = activeStyle?.style ?? ''
  const hotspotTitle = hotspot?.display_name ?? hotspot?.name ?? ''
  const modalTitle   = styleName || hotspotTitle

  // ── Style description (from interiors.json entry if present) ─────────────

  const styleDescription = (showStyleDescription && activeStyle?.description)
    ? activeStyle.description
    : null

  const imageStageStyle = isMaximized
    ? { transform: `translate(${pan.x}px, ${pan.y}px) scale(1.35)` }
    : undefined

  // ── Content ───────────────────────────────────────────────────────────────

  const imageControls = (
    <div className={styles.imageControls} onClick={(event) => event.stopPropagation()}>
      {showStyleDescription && (
        <Button
          type="button"
          kind="icon"
          size="small"
          variant="ghost"
          aria-label={t('product.hotspot_modal_style_info_aria')}
          title={styleDescription ?? styleName}
          className={styles.controlBtn}
        >
          <QuestionMarkBubble />
        </Button>
      )}
      {allowMaximize && (
        <Button
          type="button"
          kind="icon"
          size="small"
          variant="ghost"
          aria-label={t('product.hotspot_modal_maximize_aria')}
          onClick={handleMaximize}
          className={styles.controlBtn}
        >
          <Maximize />
        </Button>
      )}
    </div>
  )

  const content = isOpen && imageUrl ? (
    <>
      <div className={styles.imageContainer}>
        <InteriorImage
          src={imageUrl}
          alt={`${modalTitle} – ${styleName}`}
          imgRef={imgRef}
          isFlipped={isFlipped}
          onClick={handleImageClick}
          onPointerDown={handleImagePointerDown}
          stageStyle={imageStageStyle}
          maximized={isMaximized}
          navigator={
            allHotspots?.length > 1 && hotspot && onHotspotSelect
              ? <HotspotNavigator hotspot={hotspot} allHotspots={allHotspots} onSelect={onHotspotSelect} />
              : null
          }
        />
        {(showStyleDescription || allowMaximize) && imageControls}
      </div>

      <div className={styles.caption}>
        <p className={styles.hint}>{t('product.hotspot_modal_hint')}</p>
        {showAiDisclaimer && (
          <p className={styles.disclaimer}>{t('product.hotspot_modal_ai_disclaimer')}</p>
        )}
      </div>
    </>
  ) : null

  // ── Modal ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={modalTitle}
      className={styles.modalPanel}
    >
      <Modal.Header onClose={onClose} closeLabel={t('product.hotspot_modal_close')}>
        {modalTitle}
      </Modal.Header>
      <Modal.Content className={styles.desktopContent}>
        {content}
      </Modal.Content>
    </Modal>
  )
}
